import { useQuery } from '@apollo/client/react'
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  type RouteObject,
  RouterProvider,
  RoutesProvider,
  ScrollRestoration,
} from '@faceup/router'
import { Institution } from '@faceup/utils'
import { wrapCreateBrowserRouterV7 } from '@sentry/react'
import { useCallback, useEffect } from 'react'
import { graphql } from '../__generated__'
import { AdminLayout } from '../AdminLayout/AdminLayout'
import { PageSuspense } from '../Components/PageSuspense'
import { AdminAccessRightsProvider } from '../Contexts/AdminAccessRightsProvider'
import { routes, useRoutes } from '../hooks/useRoutes'
import { RouteNotFound } from '../RouteNotFound'
import { Auth } from '../utils/auth'
import { canAccess } from '../utils/fuAdminAccess'
import { AiPromptsView } from './AiPromptsView/AiPromptsView'
import { CreateAdminView } from './CreateAdminView'
import { HotlinesView } from './HotlinesView'
import { InstitutionDetailView } from './InstitutionDetailView'
import { InstitutionsView } from './InstitutionsView'
import { IntegrationHttpLogsView } from './IntegrationHttpLogsView'
import { InternalUsersView } from './InternalUsersView/InternalUsersView'
import { LoginRedirect } from './LoginView/LoginRedirect'
import { LoginView } from './LoginView/LoginView'
import { MaterialsView } from './MaterialsView/MaterialsView'
import { PartnerDetailView } from './PartnerDetailView'
import { PartnersView } from './PartnersView'
import { RegionMaintenanceView } from './RegionMaintenanceView'
import { ReportDetailView } from './ReportDetailView'
import { ReportsView } from './ReportsView'
import { SendEmailsView } from './SendEmailsView'
import { VonageHotlinesView } from './VonageHotlinesView'

export const query = {
  AppRouterQuery: graphql(`
    query AppRouterQuery {
      fuAdminViewer {
        id
        roles
        ...FuAdminLayout_admin
        ...FuViewerProvider_admin
      }
    }
  `),
}

const sentryCreateBrowserRouter = wrapCreateBrowserRouterV7(createBrowserRouter)

export const AppRouter = () => {
  const { client, data, dataState } = useQuery(query.AppRouterQuery, {
    skip: !Auth.getJwt(),
  })

  const routeLinks = useRoutes()

  const viewer = data?.fuAdminViewer?.__typename === 'FuAdmin' ? data.fuAdminViewer : null
  const isAuthenticated = Boolean(viewer) || Boolean(Auth.getJwt())

  const logout = useCallback(async () => {
    client.stop()
    await client.clearStore()
    Auth.logout()
    window.location.href = '/'
  }, [client])

  const hasInvalidJwt = dataState === 'complete' && !data.fuAdminViewer?.__typename
  useEffect(() => {
    if (hasInvalidJwt) {
      logout()
    }
  }, [hasInvalidJwt, logout])

  const unAuthenticatedRoutes: RouteObject[] = [
    {
      index: true,
      Component: LoginView,
    },
    {
      path: '/create-admin',
      Component: CreateAdminView,
    },
    {
      path: '*',
      Component: LoginRedirect,
    },
  ]

  const roles = viewer?.roles ?? []
  // With several roles, land on the broadest page the admin can open
  const landingPage = canAccess(roles, 'companies') ? (
    <Navigate replace to={routes => routes.companies()} />
  ) : canAccess(roles, 'schools') ? (
    <Navigate replace to={routes => routes.schools()} />
  ) : canAccess(roles, 'integrationHttpLogs') ? (
    <Navigate replace to={routes => routes.integrationHttpLogs()} />
  ) : (
    <Navigate replace to={routes => routes.companies()} />
  )

  const authenticatedRoutes: RouteObject[] = [
    {
      path: routes.home,
      element: (
        <AdminLayout logout={logout} viewer={viewer}>
          <AdminAccessRightsProvider viewer={viewer}>
            <PageSuspense>
              <Outlet />
            </PageSuspense>
          </AdminAccessRightsProvider>
        </AdminLayout>
      ),
      children: [
        {
          path: '/',
          element: landingPage,
        },
        {
          path: routes.schools,
          element: <InstitutionsView type={Institution.School} />,
        },
        {
          path: routes.companies,
          element: <InstitutionsView type={Institution.Company} />,
        },
        {
          path: routes.institution,
          Component: InstitutionDetailView,
        },
        ...(canAccess(roles, 'partners')
          ? [
              {
                path: routes.partners,
                Component: PartnersView,
              },
              {
                path: routes.partner,
                Component: PartnerDetailView,
              },
            ]
          : []),
        ...(canAccess(roles, 'materials')
          ? [
              {
                path: routes.materials,
                Component: MaterialsView,
              },
            ]
          : []),
        ...(canAccess(roles, 'cases')
          ? [
              {
                path: routes.reports,
                Component: ReportsView,
              },
              {
                path: routes.report,
                Component: ReportDetailView,
              },
            ]
          : []),
        ...(canAccess(roles, 'hotlines')
          ? [
              {
                path: routes.hotlines,
                Component: HotlinesView,
              },
            ]
          : []),
        ...(canAccess(roles, 'vonageHotlines')
          ? [
              {
                path: routes.vonageHotlines,
                Component: VonageHotlinesView,
              },
            ]
          : []),
        ...(canAccess(roles, 'aiPrompts')
          ? [
              {
                path: routes.aiPrompts,
                Component: AiPromptsView,
              },
            ]
          : []),
        ...(canAccess(roles, 'sendEmails')
          ? [
              {
                path: routes.sendEmails,
                Component: SendEmailsView,
              },
            ]
          : []),
        ...(canAccess(roles, 'regionMaintenance')
          ? [
              {
                path: routes.regionMaintenance,
                Component: RegionMaintenanceView,
              },
            ]
          : []),
        ...(canAccess(roles, 'integrationHttpLogs')
          ? [
              {
                path: routes.integrationHttpLogs,
                Component: IntegrationHttpLogsView,
              },
            ]
          : []),
        ...(canAccess(roles, 'internalUsers')
          ? [
              {
                path: routes.internalUsers,
                Component: InternalUsersView,
              },
            ]
          : []),
        {
          path: '*',
          Component: RouteNotFound,
        },
      ],
    },
  ]

  const router = sentryCreateBrowserRouter([
    {
      path: '/',
      element: (
        <RoutesProvider routes={routeLinks}>
          <ScrollRestoration />
          <Outlet />
        </RoutesProvider>
      ),
      children: isAuthenticated ? authenticatedRoutes : unAuthenticatedRoutes,
    },
  ])

  return <RouterProvider router={router} />
}
